// Partner API client (same origin, /api/v1/partner). The access token lives only in memory; the refresh
// token is an httpOnly cookie (tcrm_prt) that the browser sends to auth/refresh with the CSRF header.
// A 401 triggers one silent refresh and a single retry.

export interface Problem {
  title: string;
  status: number;
  detail?: string;
  code?: string;
  errors?: { field: string; message: string }[];
}

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }
  get status() {
    return this.problem.status;
  }
  get code() {
    return this.problem.code;
  }
  fieldError(field: string): string | undefined {
    return this.problem.errors?.find((e) => e.field === field)?.message;
  }
}

const BASE = '/api/v1/partner';
let accessToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(signedIn: boolean) => void>();

export function onSessionChange(fn: (signedIn: boolean) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setSession(token: string | null, expiresIn?: number): void {
  accessToken = token;
  clearTimeout(refreshTimer);
  if (token && expiresIn) {
    refreshTimer = setTimeout(() => void refreshSession(), Math.max(expiresIn - 60, 30) * 1000);
  }
  listeners.forEach((l) => l(!!token));
}

export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'x-tatra-csrf': '1' },
        credentials: 'same-origin',
      });
      if (!res.ok) {
        setSession(null);
        return false;
      }
      const body = (await res.json()) as { accessToken: string; expiresIn: number };
      setSession(body.accessToken, body.expiresIn);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function toProblem(res: Response): Promise<Problem> {
  try {
    const body = (await res.json()) as Partial<Problem>;
    return { title: body.title ?? res.statusText, status: res.status, ...body } as Problem;
  } catch {
    return { title: res.statusText || 'Request failed', status: res.status };
  }
}

type Method = 'GET' | 'POST' | 'PATCH';

export async function api<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const form = body instanceof FormData;
  const send = () =>
    fetch(`${BASE}${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(body !== undefined && !form ? { 'Content-Type': 'application/json' } : {}),
        // Calls that carry the refresh cookie need the CSRF header.
        ...(path.startsWith('/auth/') ? { 'x-tatra-csrf': '1' } : {}),
      },
      body: body === undefined ? undefined : form ? body : JSON.stringify(body),
    });
  let res = await send();
  if (res.status === 401 && accessToken && !path.startsWith('/auth/')) {
    if (await refreshSession()) res = await send();
  }
  if (!res.ok) throw new ApiError(await toProblem(res));
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  return (type.includes('json') ? await res.json() : await res.text()) as T;
}

export const get = <T>(path: string) => api<T>('GET', path);
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body ?? {});
export const patch = <T>(path: string, body: unknown) => api<T>('PATCH', path, body);

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.problem.detail ?? e.problem.title;
  if (e instanceof Error) return e.message;
  return 'Something went wrong';
}

/** Opens a claim invoice (needs the bearer token, so not a plain link). */
export async function openInvoice(claimId: string): Promise<void> {
  const send = () =>
    fetch(`${BASE}/claims/${claimId}/invoice`, {
      credentials: 'same-origin',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
  let res = await send();
  if (res.status === 401 && (await refreshSession())) res = await send();
  if (!res.ok) throw new ApiError(await toProblem(res));
  const url = URL.createObjectURL(await res.blob());
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ───────────────────────────── shapes returned by the API

export interface PortalConfig {
  brandName: string;
  primaryColour: string | null;
  hasLogo: boolean;
  locale: string;
  timezone: string;
  selfSignUp: boolean;
  exclusivityDays: number;
  /** Commission and claims shown (the organisation handles money in the CRM, D-122). */
  money?: boolean;
}

export type FirmStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';

export interface Me {
  id: string;
  name: string;
  kind: string;
  status: FirmStatus;
  contactName: string;
  email: string;
  phone: string | null;
  city: string | null;
  address: string | null;
  kyc: { pan: string | null; gstin: string | null; bankAccount: string | null };
  bankAccountName: string | null;
  bankIfsc: string | null;
  bankName: string | null;
  reraNumber: string | null;
  reraState: string | null;
  reraValidUntil: string | null;
  decisionNote: string | null;
  projects: { id: string; name: string }[];
  missing: string[];
  me: { id: string; name: string; email: string; role: 'OWNER' | 'AGENT' };
}

export interface Project {
  id: string;
  name: string;
  city: string | null;
  reraNumber: string | null;
}

export type RegStatus = 'EXCLUSIVE' | 'LAPSED' | 'NOT_ACCEPTED';

export interface LeadRow {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  project: { id: string; name: string };
  registeredBy: string;
  registeredAt: string;
  exclusiveUntil: string | null;
  status: RegStatus;
  stage: string | null;
  outcome: string | null;
  visits: number;
  bookings: number;
}

export interface LeadDetail extends Omit<LeadRow, 'visits' | 'bookings'> {
  notes: string | null;
  lastVisitAt?: string | null;
  visits: { id: string; scheduledAt: string; status: string; project: string }[];
  bookings: { bookedOn: string; project: string; cancelled: boolean; commission: number }[];
}

export interface Entry {
  id: string;
  kind: string;
  status: string;
  basis: number;
  amount: number;
  note: string | null;
  claimId: string | null;
  createdAt: string;
  booking: { bookedOn: string; cancelled: boolean; project: string };
  customer: string;
  plan: string | null;
}

export interface Ledger {
  balance: { unclaimed: number; claimed: number; approved: number; paid: number } | null;
  entries: Entry[];
}

export interface Claim {
  id: string;
  number: string;
  status: 'CLAIMED' | 'APPROVED' | 'REJECTED' | 'INVOICED' | 'PAID';
  amount: number;
  partnerNote: string | null;
  decisionNote: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  invoiceAmount: number | null;
  hasInvoice: boolean;
  tdsAmount: number | null;
  netPaid: number | null;
  paidOn: string | null;
  paymentReference: string | null;
  createdAt: string;
  entries: number;
}

export interface Scorecard {
  leads: number;
  refused: number;
  visits: number;
  visitedLeads: number;
  bookings: number;
  cancelledBookings: number;
  conversion: number;
  commissionEarned: number;
  commissionPaid: number;
  commissionOutstanding: number;
}

export interface Broadcast {
  id: string;
  kind: string;
  title: string;
  body: string;
  sentAt: string;
  readAt?: string | null;
  projects: string[];
}

export interface TeamUser {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: 'OWNER' | 'AGENT';
  status: string;
  lastLoginAt: string | null;
}
